import type { ContourId, ContourLabel } from './contour-definitions';
import type { Point, PointArray } from './types/contour-definition-array-type';

import LZString from 'lz-string';

import { defineStore } from 'pinia';
import { markRaw, nextTick } from 'vue';

import { getMessage } from '@/src/utils/exception';

import { useCurrentStudyStore } from '../dicom-web/current-study-store';
import { ContourContainer, SerializedContourContainerSchema } from './contour-container';
import * as ContourDefinitions from './contour-definitions';
import { useContourEventStore } from './contour-event-store';

const MAX_HISTORY_LENGTH = 10;

interface UndoRedoCommand {
  execute: (isRedo?: boolean) => void; // support redo flag
  undo: () => void;
  description: string;
}

interface AddContourCommand extends UndoRedoCommand {
  type: 'add';
  UID: string;
  contourId: ContourId;
  points: PointArray;
  props: ContourDefinitions.ContourProps;
}

interface DeleteContourCommand extends UndoRedoCommand {
  type: 'delete';
  UID: string;
  contourId: ContourId;
  savedContour?: any; // Store the deleted contour for restoration
}

interface TranslateContourCommand extends UndoRedoCommand {
  type: 'translate';
  UID: string;
  contourId: ContourId;
  distancePoint: [number, number];
}

interface BatchCommand extends UndoRedoCommand {
  type: 'batch';
  commands: UndoRedoCommand[];
}

interface DeleteAllContoursCommand extends UndoRedoCommand {
  type: 'deleteAll';
  UID: string;
  savedContours: Array<{
    points: PointArray;
    props: ContourDefinitions.ContourProps;
  }>;
}

interface State {
  contourContainers: Record<string, ContourContainer>;
  contourPathsCache: Record<string, string>;
  commands: UndoRedoCommand[];
  currentCommandIndex: number;
  history: Array<{
    state: string;
    timestamp: number;
  }>;
  copyTemp: Array<{
    points: PointArray;
    label: ContourDefinitions.ContourLabel;
    id: ContourDefinitions.ContourLabelItemId;
    replace?: boolean;
    text?: string;
    sourceImageUID?: string;
  }>;
  currentHistoryIndex: number;
}

export const useContourContainerStore = defineStore('contourContainerStore', {
  state: (): State => ({
    contourContainers: {},
    contourPathsCache: {},
    commands: [],
    currentCommandIndex: -1, // -1 means no commands executed yet
    history: [
      {
        state: LZString.compress(JSON.stringify({})),
        // state: {},  // Initialize with empty state
        timestamp: Date.now(),
      },
    ],
    copyTemp: [],
    currentHistoryIndex: 0,
  }),
  getters: {
    hasPast: state => state.currentCommandIndex >= 0,
    hasFuture: state => state.currentCommandIndex < state.commands.length - 1,
  },
  actions: {
    // Helper to execute command and save to history
    executeCommand(command: UndoRedoCommand) {
      // Remove any "future" commands if user undid then makes a new edit
      this.commands.splice(this.currentCommandIndex + 1);

      // Execute the command
      command.execute();

      // Add to history
      this.commands.push(command);

      // Cap length
      if (this.commands.length > MAX_HISTORY_LENGTH) {
        this.commands.shift();
      }
      else {
        this.currentCommandIndex++;
      }
    },
    addContourToCache(UID: string, contourId: ContourId, contour: string) {
      if (!UID || !contourId) return;

      const cacheKey = `${UID}_${contourId.label}_${contourId.id}`;
      this.contourPathsCache[cacheKey] = contour;
    },

    getContourFromCache(UID: string, contourId: ContourId) {
      if (!UID || !contourId) return null;

      const cacheKey = `${UID}_${contourId.label}_${contourId.id}`;

      return this.contourPathsCache[cacheKey] || null;
    },

    addContour(
      UID: string,
      points: PointArray,
      label: ContourDefinitions.ContourLabel,
      id: ContourDefinitions.ContourLabelItemId = ContourDefinitions.encodeContourLabelItemId(
        [-1],
      ),
      triggerEvent: boolean = true,
      saveHistory: boolean = true,
      replace: boolean = false,
      text: string = '',
    ) {
      if (!label) return;

      if (!this.contourContainers[UID]) {
        this.contourContainers[UID] = new ContourContainer(UID);
      }

      const contourProps = { ...ContourDefinitions.ContourDefinitions[label] };
      if (text && text.length > 0) {
        contourProps.text = text;
      }
      contourProps.imageUID = UID;
      contourProps.id = id;

      const contourExists = this.isPresentContourLabel(UID, label);

      const savedContours = this.getContourOfLabel(UID, label);

      if (saveHistory) {
        const command: AddContourCommand = {
          type: 'add',
          UID,
          contourId: { label, id },
          points: [...points], // Clone points
          props: { ...contourProps },
          description: `Add ${label} contour. ID: ${id}. Exists: ${contourExists}`,

          execute: () => {
            if (!this.contourContainers[UID]) {
              this.contourContainers[UID] = new ContourContainer(UID);
            }
            const added = this.contourContainers[UID].addUserContourPolygon(
              points,
              contourProps,
              replace,
            );
            if (!added) {
              // If the contour was not added, remove the history entry:
              this.commands.pop();
              this.currentCommandIndex--;
              return;
            }

            if (triggerEvent) {
              useContourEventStore().emitChange('add', UID, label, id);
            }
          },

          undo: () => {
            if (contourExists) {
              if (!savedContours || savedContours.length === 0) {
                console.error('Failed to restore a Contour!', label, id, UID);
                return;
              }
              this.deleteAllContoursOfLabel(
                UID,
                label,
                false, // Do not trigger event on undo
              );
              savedContours.forEach(({ points: savedPoints, props }) => {
                this.contourContainers[UID].addUserContourPolygon(
                  savedPoints,
                  props,
                  true, // replace
                  false, // do not enforce hierarchy rules on restore
                );
              });
            }
            else {
              this.contourContainers[UID].deleteContour({
                label,
                id: '0' as ContourDefinitions.ContourLabelItemId,
              });
            }

            if (triggerEvent) {
              useContourEventStore().emitChange('add', UID, label, id);
            }
          },
        };

        this.executeCommand(command);
      }
      else {
        // Direct execution without history
        const changed = this.contourContainers[UID].addUserContourPolygon(
          points,
          contourProps,
          replace,
        );
        if (changed) {
          if (triggerEvent) {
            useContourEventStore().emitChange('add', UID, label, id);
          }
        }
      }
    },

    addContoursBatch(
      contours: Array<{
        UID: string;
        points: PointArray;
        label: ContourDefinitions.ContourLabel;
        id?: ContourDefinitions.ContourLabelItemId;
        replace?: boolean;
        text?: string;
      }>,
    ) {
      if (!contours.length) return;

      // Create individual commands for each contour
      const individualCommands: AddContourCommand[] = contours.map(
        ({ UID, points, label, id, replace, text }) => {
          const actualId
            = id !== undefined
              ? id
              : ContourDefinitions.encodeContourLabelItemId([-1]);
          const contourProps = {
            ...ContourDefinitions.ContourDefinitions[label],
          };
          if (text && text.length > 0) {
            contourProps.text = text;
          }
          contourProps.imageUID = UID;
          contourProps.id = actualId;

          return {
            type: 'add',
            UID,
            contourId: { label, id: actualId },
            points: [...points],
            props: { ...contourProps },
            description: `Add ${label} contour (batch)`,

            execute: () => {
              if (!this.contourContainers[UID]) {
                this.contourContainers[UID] = new ContourContainer(UID);
              }
              this.contourContainers[UID].addUserContourPolygon(
                points,
                contourProps,
                replace || false,
                false, // batch adds bypass interactive hierarchy enforcement
              );
            },

            undo: () => {
              // if (this.contourContainers[UID]) {
              this.contourContainers[UID].deleteContour({
                label,
                id: actualId,
              });
              // }
            },
          };
        },
      );

      // Create batch command
      const batchCommand: BatchCommand = {
        type: 'batch',
        commands: individualCommands,
        description: `Add ${contours.length} contours`,

        execute: () => {
          // Get all unique UIDs involved in this batch
          const uniqueUIDs = Array.from(new Set(contours.map(c => c.UID)));
          this.$patch((state) => {
            // Ensure containers exist

            for (const contour of contours) {
              if (!state.contourContainers[contour.UID]) {
                state.contourContainers[contour.UID] = markRaw(
                  new ContourContainer(contour.UID),
                );
              }
            }

            // Execute all individual commands

            for (const cmd of individualCommands) {
              cmd.execute();
            }
          });

          void nextTick(() => {
            useContourEventStore().emitChange('batch', [...uniqueUIDs]);
          });
        },

        undo: () => {
          const uniqueUIDs = Array.from(new Set(contours.map(c => c.UID)));
          // Undo in reverse order
          for (let i = individualCommands.length - 1; i >= 0; i--) {
            individualCommands[i].undo();
          }
          useContourEventStore().emitChange('batch', [...uniqueUIDs]);
        },
      };

      this.executeCommand(batchCommand);
    },

    isPresentContourLabel(UID: string, label: ContourLabel) {
      if (!this.contourContainers[UID]) return false;
      return this.contourContainers[UID].isPresentContourLabel(label);
    },

    isPresentContourContourId(UID: string, contourId: ContourId) {
      if (!this.contourContainers[UID]) return false;
      return this.contourContainers[UID].isPresentContourId(contourId);
    },

    translateContour(
      UID: string,
      contourId: ContourId,
      distancePoint: [number, number],
      triggerEvent: boolean = true,
    ) {
      if (!this.contourContainers[UID]) return;

      const command: TranslateContourCommand = {
        type: 'translate',
        UID,
        contourId,
        distancePoint,
        description: `Translate ${contourId.label} contour`,

        execute: () => {
          this.contourContainers[UID]?.translateContour(
            contourId,
            distancePoint,
          );
          if (triggerEvent) {
            useContourEventStore().emitChange(
              'translate',
              UID,
              contourId.label,
            );
          }
        },

        undo: () => {
          // Translate back by negating the distance
          const reverseDistance: [number, number] = [
            -distancePoint[0],
            -distancePoint[1],
          ];
          this.contourContainers[UID]?.translateContour(
            contourId,
            reverseDistance,
            false, // do not enforce hierarchy rules on undo
          );
          if (triggerEvent) {
            useContourEventStore().emitChange(
              'translate',
              UID,
              contourId.label,
            );
          }
        },
      };

      this.executeCommand(command);
    },

    smoothContour(
      UID: string,
      contourId: ContourId,
      method = 'gaussian',
      strength = 1.5,
      triggerEvent: boolean = true,
    ) {
      if (!this.contourContainers[UID]) return;

      const contour = this.contourContainers[UID].getContour(contourId);
      if (!contour) return;

      contour.smoothContour(method, strength);
      this.contourContainers[UID].resetDependentContours(contour.getProps());
      if (triggerEvent) {
        useContourEventStore().emitChange('translate', UID, contourId.label);
      }
    },

    roundContour(
      UID: string,
      contourId: ContourId,
      triggerEvent: boolean = true,
    ) {
      if (!this.contourContainers[UID]) return;

      const contour = this.contourContainers[UID].getContour(contourId);
      if (!contour) return;

      contour.roundContour();
      this.contourContainers[UID].resetDependentContours(contour.getProps());
      if (triggerEvent) {
        useContourEventStore().emitChange('translate', UID, contourId.label);
      }
    },

    scaleContour(
      UID: string,
      contourId: ContourId,
      percentChange: number = 5,
      triggerEvent: boolean = true,
      saveHistory: boolean = true,
    ) {
      if (!this.contourContainers[UID]) return false;

      const contour = this.contourContainers[UID].getContour(contourId);
      if (!contour || contour.getProps().userEditable === false) return false;

      if (saveHistory) {
        const prevPolygon = contour.getPolygon().map((p: Point) => ({ x: p.x, y: p.y }));
        let nextPolygon: PointArray | null = null;
        let success = false;

        const command = {
          execute: () => {
            const container = this.contourContainers[UID];
            if (!container) return;
            const c = container.getContour(contourId);
            if (!c) return;

            if (nextPolygon) {
              c.setPolygon(nextPolygon.map((p: Point) => ({ x: p.x, y: p.y })));
              container.resetDependentContours(c.getProps());
            }
            else {
              success = container.scaleContour(contourId, percentChange, true);
              if (success) {
                nextPolygon = c.getPolygon().map((p: Point) => ({ x: p.x, y: p.y }));
              }
            }
            if (triggerEvent) {
              useContourEventStore().emitChange('translate', UID, contourId.label);
            }
          },
          undo: () => {
            const container = this.contourContainers[UID];
            if (!container) return;
            const c = container.getContour(contourId);
            if (!c) return;

            c.setPolygon(prevPolygon.map((p: Point) => ({ x: p.x, y: p.y })));
            container.resetDependentContours(c.getProps());
            if (triggerEvent) {
              useContourEventStore().emitChange('translate', UID, contourId.label);
            }
          },
          description: `Scale ${contourId.label} contour by ${percentChange}%`,
        };

        this.executeCommand(command);
        if (!success) {
          this.commands.pop();
          this.currentCommandIndex--;
          return false;
        }
        return true;
      }
      else {
        const success = this.contourContainers[UID].scaleContour(
          contourId,
          percentChange,
          true,
        );
        if (triggerEvent) {
          useContourEventStore().emitChange('translate', UID, contourId.label);
        }
        return success;
      }
    },

    growContour(
      UID: string,
      contourId: ContourId,
      percent: number = 5,
      triggerEvent: boolean = true,
      saveHistory: boolean = true,
    ) {
      return this.scaleContour(UID, contourId, Math.abs(percent), triggerEvent, saveHistory);
    },

    shrinkContour(
      UID: string,
      contourId: ContourId,
      percent: number = 5,
      triggerEvent: boolean = true,
      saveHistory: boolean = true,
    ) {
      return this.scaleContour(UID, contourId, -Math.abs(percent), triggerEvent, saveHistory);
    },

    deleteContour(
      UID: string,
      contourId: ContourId,
      triggerEvent: boolean = true,
      saveHistory: boolean = true,
    ) {
      if (!this.contourContainers[UID]) return;

      if (saveHistory) {
        // Save the contour before deleting for undo
        const savedContour = this.contourContainers[UID].getContour(contourId);
        if (!savedContour) return;

        const savedPoints = savedContour.getPolygonScaled();
        const savedProps = savedContour.getProps();

        const command: DeleteContourCommand = {
          type: 'delete',
          UID,
          contourId,
          description: `Delete ${contourId.id}, ${contourId.label} contour`,

          execute: (isRedo?: boolean) => {
            this.contourContainers[UID]?.deleteContour(contourId);
            if (triggerEvent || isRedo) {
              // changed: always emit on redo
              // const { seriesIndices } =
              //   useCurrentStudyStore().imageInfoDicom[UID];
              useContourEventStore().emitChange('delete', UID, contourId.label);
            }
          },

          undo: () => {
            if (!this.contourContainers[UID]) {
              this.contourContainers[UID] = new ContourContainer(UID);
            }
            this.contourContainers[UID].addUserContourPolygon(
              savedPoints,
              savedProps,
              true,
              false, // do not enforce hierarchy rules on restore
            );
            if (triggerEvent) {
              // const { seriesIndices } =
              //   useCurrentStudyStore().imageInfoDicom[UID];
              useContourEventStore().emitChange('add', UID, contourId.label);
            }
          },
        };

        this.executeCommand(command);
      }
      else {
        // Direct execution
        this.contourContainers[UID].deleteContour(contourId);
        if (triggerEvent) {
          // const { seriesIndices } = useCurrentStudyStore().imageInfoDicom[UID];
          useContourEventStore().emitChange('delete', UID, contourId.label);
        }
      }
    },

    deleteAllContours(UID: string, triggerEvent: boolean = true) {
      if (!this.contourContainers[UID]) return;
      // Save all contours before deleting for undo
      const savedContours: Array<{
        points: PointArray;
        props: ContourDefinitions.ContourProps;
      }> = [];

      const contours = this.contourContainers[UID].getDrawingContours();
      if (contours) {
        for (const [, contourMap] of Object.entries(contours)) {
          for (const [, contour] of Object.entries(contourMap)) {
            const contourArray = contour.getPolygonScaled();
            const props = contour.getProps();
            savedContours.push({
              points: [...contourArray],
              props: { ...props },
            });
          }
        }
      }

      if (savedContours.length === 0) {
        return;
      }

      const command: DeleteAllContoursCommand = {
        type: 'deleteAll',
        UID,
        savedContours,
        description: `Delete all contours for ${UID}`,

        execute: (isRedo?: boolean) => {
          this.contourContainers[UID]?.deleteAllContours();
          if (triggerEvent || isRedo) {
            // changed: always emit on redo
            // const { seriesIndices } =
            //   useCurrentStudyStore().imageInfoDicom[UID];
            useContourEventStore().emitChange('deleteAll', UID);
          }
        },

        undo: () => {
          // Restore all deleted contours without adding to history
          if (savedContours.length === 0) return;

          // Make sure container exists
          if (!this.contourContainers[UID]) {
            this.contourContainers[UID] = new ContourContainer(UID);
          }

          // Restore each contour individually to avoid batch operation's history recording
          savedContours.forEach(({ points, props }) => {
            this.contourContainers[UID].addUserContourPolygon(
              points,
              props,
              true, // replace
              false, // do not enforce hierarchy rules on restore
            );
          });
          // const { seriesIndices } = useCurrentStudyStore().imageInfoDicom[UID];
          useContourEventStore().emitChange('add', UID);
        },
      };
      this.executeCommand(command);
    },

    deleteAllContoursOfLabel(
      UID: string,
      label: ContourLabel,
      triggerEvent: boolean = true,
    ) {
      if (!this.contourContainers[UID]) return;
      this.contourContainers[UID].deleteAllContoursOfLabel(label);
      if (triggerEvent) {
        useContourEventStore().emitChange('deleteAll', UID);
      }
    },

    bulkDeleteContour(
      UIDs: string[],
      contourId: ContourId,
      triggerEvent: boolean = true,
    ) {
      for (const UID of UIDs) {
        if (!this.contourContainers[UID]) continue;
        // Check if contour exists before deleting

        if (!this.isPresentContourContourId(UID, contourId)) continue;
        this.deleteContour(UID, contourId, false);
      }
      if (triggerEvent) {
        // ToDo clarify data for event
        useContourEventStore().emitChange('deleteAll', UIDs);
      }
    },

    // ToDO: Known issue when bulk deleting contours, the redo event would not emit ContourChange. As such contours might now render right away.
    bulkDeleteAllContours(UIDs: string[], triggerEvent: boolean = true) {
      for (const UID of UIDs) {
        if (!this.contourContainers[UID]) continue;
        this.deleteAllContours(UID, false);
      }
      if (triggerEvent) {
        // ToDo clarify data for event
        useContourEventStore().emitChange('deleteAll', UIDs);
      }
    },

    getContour(UID: string, contourId: ContourId) {
      if (!this.contourContainers[UID]) return null;

      const contour = this.contourContainers[UID]?.getContour(contourId);
      const polygon = contour?.getPolygonScaled();

      return polygon;
    },

    getContourOfLabel(UID: string, label: ContourLabel) {
      const savedContours = [] as Array<{
        points: PointArray;
        props: ContourDefinitions.ContourProps;
      }>;
      const contours = this.contourContainers[UID]?.getDrawingContours();
      if (contours) {
        for (const [, contourMap] of Object.entries(contours)) {
          for (const [, contour] of Object.entries(contourMap)) {
            const contourArray = contour.getPolygonScaled();
            const props = contour.getProps();
            if (props.label === label) {
              savedContours.push({
                points: [...contourArray],
                props: { ...props },
              });
            }
          }
        }
      }
      return savedContours;
    },

    getDrawingContours(UID: string) {
      if (!this.contourContainers[UID]) return null;
      return this.contourContainers[UID].getDrawingContours();
    },

    hasContours(UID: string) {
      const contours = this.getDrawingContours(UID);
      return (
        contours !== null
        && Object.keys(contours).length > 0
        && Object.values(contours).some(
          contourMap => Object.keys(contourMap).length > 0,
        )
      );
    },

    addContoursToTempCopy(
      imageUID: string,
      targetLabel?: ContourLabel,
      targetId?: ContourDefinitions.ContourLabelItemId,
    ) {
      this.clearTempCopy();
      const contours = this.getDrawingContours(imageUID);

      if (contours) {
        for (const [label, contourMap] of Object.entries(contours)) {
          if (targetLabel && label !== targetLabel) {
            continue; // Skip non-matching labels if targetLabel is specified
          }

          for (const [id, contour] of Object.entries(contourMap)) {
            if (targetId !== undefined && targetId !== null && id !== String(targetId)) {
              continue;
            }
            // If label is text, also copy the text property
            let text = '';
            if (label === ContourDefinitions.ContourLabel.AnnotationText) {
              const props = contour.getProps();
              text = props.text || '';
            }
            const contourArray = contour.getPolygonScaled();
            this.copyTemp.push({
              points: contourArray,
              label: label as ContourLabel,
              id: id as ContourDefinitions.ContourLabelItemId,
              replace: true,
              text,
              sourceImageUID: imageUID,
            });
          }
        }
      }
    },
    getTempCopyContours() {
      return this.copyTemp;
    },
    clearTempCopy() {
      this.copyTemp = [];
    },
    pasteTempCopyContours(UID: string) {
      const finalBatch = [];

      for (const contour of this.copyTemp) {
        finalBatch.push({
          UID,
          points: contour.points,
          label: contour.label,
          id: contour.id,
          replace: true,
          text: contour.text,
        });
      }

      this.addContoursBatch(finalBatch);
      useContourEventStore().emitChange('copy', UID);

      // this.clearTempCopy(); // Clear temp copy after pasting if needed
    },

    pasteTempsCopyContoursMultiple(UIDs: string[], srcUID?: string) {
      const finalBatch = [];

      for (const UID of UIDs) {
        for (const contour of this.copyTemp) {
          finalBatch.push({
            UID,
            points: contour.points,
            label: contour.label,
            id: contour.id,
            replace: true,
            text: contour.text,
          });
        }
      }

      this.addContoursBatch(finalBatch);
      useContourEventStore().emitChange(
        'copy',
        srcUID ? [srcUID, ...UIDs] : UIDs,
      );

      this.clearTempCopy();
    },

    getContourContainer(UID: string) {
      if (!this.contourContainers[UID]) return null;
      return this.contourContainers[UID];
    },

    serialize() {
      const serializedData: Record<string, any> = {};

      for (const [uid, container] of Object.entries(this.contourContainers)) {
        serializedData[uid] = container.serialize();
      }
      return serializedData;
    },

    deserialize(data: Record<string, any>) {
      const rawData = data;
      // Clear existing keys without replacing the entire object
      Object.keys(this.contourContainers).forEach((key) => {
        delete this.contourContainers[key]; // Manual cleanup
      });

      // Assign new data, marking only new objects as raw to avoid reactivity loops
      Object.entries(rawData).forEach(([uid, containerData]) => {
        try {
          const validatedContainerData = SerializedContourContainerSchema.parse(containerData);
          this.contourContainers[uid] = markRaw(
            ContourContainer.deserialize(validatedContainerData),
          );
        }
        catch (error) {
          console.error('Error parsing contour container data (%s)', getMessage(error));
        }
      });
      useContourEventStore().emitChange('import', []);
    },

    // Optional helper methods for file operations
    async saveToFile() {
      const studyID = useCurrentStudyStore().getStudyID;
      const filename = `${studyID}_contours.json`;

      // Save file
      const data = JSON.stringify(this.serialize(), null, 2);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    async loadFromFile() {
      const studyID = useCurrentStudyStore().getStudyID;
      const expectedFilename = `${studyID}_contours.json`;

      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';
      input.multiple = false;
      input.style.display = 'none'; // Hide the input

      // Show an alert or UI message prompting user to select the correct file
      // eslint-disable-next-line no-alert
      alert(`Please select the file: ${expectedFilename}`);

      const promise = new Promise((resolve, reject) => {
        input.onchange = async (e) => {
          const file = (e.target as HTMLInputElement).files?.[0];
          if (file) {
            if (file.name !== expectedFilename) {
              // eslint-disable-next-line no-alert
              alert(
                `Warning: You selected "${file.name}". Expected "${expectedFilename}".`,
              );
            }
            try {
              const text = await file.text();
              resolve(JSON.parse(text));
            }
            catch /* (error) */ {
              reject(new Error('Invalid JSON format.'));
            }
          }
          else {
            reject(new Error('No file selected'));
          }
        };
      });

      document.body.appendChild(input); // Add to document
      input.click(); // Trigger file dialog
      const data = (await promise) as Record<string, any>;

      this.deserialize(data);
    },

    // Add undo/redo methods
    undo() {
      if (!this.hasPast) return false;
      const command = this.commands[this.currentCommandIndex];
      command.undo();
      this.currentCommandIndex--;
      return true;
    },

    redo() {
      if (!this.hasFuture) return false;
      this.currentCommandIndex++;
      const command = this.commands[this.currentCommandIndex];
      command.execute(true); // mark as redo
      return true;
    },
  },
});
